import { useEffect, useMemo, useRef, useState } from 'react';
import { PhotoModel } from '../../../types/protocols';
import { getImagePathWithSize, ImageSize } from '../../../Helpers/ImageLoader';
import { PhotoGroupItem } from '../../WorkOrders/components/shared';
import { compareNewestFirst } from '../../../Helpers/photoOrdering';

type UrlResolver = (storagePath: string) => Promise<string>;

/** Resolves once the browser has tried to load `url`: true if it loaded. */
const imageLoads = (url: string): Promise<boolean> =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
    });

/** Waits before each retry of a thumbnail that did not load; the first try is immediate. */
export const THUMBNAIL_RETRY_DELAYS_MS = [1000, 2000, 4000];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves signed thumbnail URLs for a photo map, newest first. The guest API
 * signs a URL whether or not the object exists, and thumbnails are generated
 * asynchronously after upload, so a just-uploaded photo's thumbnail URL 404s
 * for a few seconds. The thumbnail is therefore loaded here and retried after
 * THUMBNAIL_RETRY_DELAYS_MS, each time with a newly signed URL so the browser
 * cannot reuse the failed load; the original is used when it never loads, or
 * at once when the thumbnail path is refused.
 */
export function useGuestPhotoItems(
    photos: { [key: string]: PhotoModel } | undefined,
    getDownloadUrl: UrlResolver,
    canRemove: (author: string) => boolean,
): PhotoGroupItem[] {
    const [urls, setUrls] = useState<Record<string, string>>({});
    const requested = useRef(new Set<string>());
    // Mount-scoped, not per effect run: a refetch re-runs the effect while
    // earlier requests are in flight, and their keys are never requested again.
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const list = useMemo(
        () => Object.values(photos || {}).filter((p) => p && p.key && p.storagePath).sort(compareNewestFirst),
        [photos],
    );
    useEffect(() => {
        list.forEach((photo) => {
            if (requested.current.has(photo.key)) return;
            requested.current.add(photo.key);
            const original = photo.storagePath;
            const thumb = getImagePathWithSize(original, ImageSize.THUMBNAIL_SMALL) || original;
            const resolveThumbnail = async (): Promise<string | null> => {
                for (const delay of [0, ...THUMBNAIL_RETRY_DELAYS_MS]) {
                    if (delay) await wait(delay);
                    if (!mounted.current) return null;
                    const url = await getDownloadUrl(thumb);
                    if (await imageLoads(url)) return url;
                }
                return null;
            };
            const resolved = thumb === original
                ? getDownloadUrl(original)
                : resolveThumbnail()
                    .catch(() => null)
                    .then((url) => url ?? (mounted.current ? getDownloadUrl(original) : Promise.reject(new Error('unmounted'))));
            resolved
                .then((url) => { if (mounted.current) setUrls((prev) => (prev[photo.key] ? prev : { ...prev, [photo.key]: url })); })
                .catch(() => { /* broken image placeholder is rendered by the leaf */ });
        });
    }, [list, getDownloadUrl]);
    return useMemo(
        () => list.map((p) => ({ key: p.key, url: urls[p.key] || '', canRemove: canRemove(p.author), date: p.date })),
        [list, urls, canRemove],
    );
}
